import {environment} from '../../../environments/environment';
import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpHeaders, HttpParams} from '@angular/common/http';
import {EMPTY, Observable, BehaviorSubject, timer} from 'rxjs';
import {catchError, map, debounceTime, switchMap} from 'rxjs/operators';
import {ChatbotChat, ChatbotChatCreate} from '../state/chatbot-chat';
import {ChatbotChatRepresentation, ChatPaginatedResponse} from '../state/chatbot-chat-representation';
import {Id37Router} from '../../id37-router';


const API_URL = environment.CHATBOT_API;

export interface PaginationState {
  loading: boolean;
  hasMore: boolean;
  currentPage: number;
  pageSize: number;
  nextCursor: string | null;
  error?: any;
}

@Injectable({
  providedIn: 'root'
})
export class ChatbotChatService {

  private chatsSubject$ = new BehaviorSubject<ChatbotChat[]>([]);
  private loadingSubject$ = new BehaviorSubject<boolean>(false);
  private isInitialLoadingSubject$ = new BehaviorSubject<boolean>(false);
  private paginationSubject$ = new BehaviorSubject<PaginationState>({
    loading: false,
    hasMore: true,
    currentPage: 0,
    pageSize: 12,
    nextCursor: null
  });

  public readonly chats$ = this.chatsSubject$.asObservable();
  public readonly loading$ = this.loadingSubject$.asObservable();
  public readonly pagination$ = this.paginationSubject$.asObservable();
  public readonly isInitialLoading$ = this.isInitialLoadingSubject$.asObservable();

  private refreshSubject$ = new BehaviorSubject<void>(undefined);

  constructor(private http: HttpClient, private router: Id37Router) {
    this.refreshSubject$.pipe(
      debounceTime(300),
      switchMap(() => this.loadInitialChats())
    ).subscribe();
  }

  public addChat(create_chat: ChatbotChatCreate): Observable<ChatbotChat> {
    return this.http.post<ChatbotChat>(
      API_URL + '/chats/add',
      {
        user_id: create_chat.user_id
      }
    ).pipe(map((rep: ChatbotChatRepresentation) => ChatbotChat.buildFrom(rep)));
  }

  public rateChat(chat_id: number, chat_rating: number): Observable<ChatbotChat> {
    return this.http.post<ChatbotChat>(
      API_URL + '/chats/rating',
      {chat_id, chat_rating}
    ).pipe(map((rep: ChatbotChatRepresentation) => ChatbotChat.buildFrom(rep)));
  }

  public getChatByID(id: number): Observable<ChatbotChat> {
    return this.http.get<ChatbotChat>(
      API_URL + '/chats/get/chat/' + id.toString(10),
      {
        headers: new HttpHeaders({
          Accept: 'application/json;'
        })
      }
    ).pipe(
      map((chatbotChatRepresentation: ChatbotChatRepresentation) =>
        ChatbotChat.buildFrom(chatbotChatRepresentation)
      ));
  }

  public getChatList(): Observable<ChatbotChat[]> {
    return this.http.get<ChatbotChat[]>(
      API_URL + '/chats/list',
      {
        headers: new HttpHeaders({
          Accept: 'application/json;'
        })
      }
    ).pipe(map((chatbotChatRepresentations: ChatbotChatRepresentation[]) =>
      chatbotChatRepresentations.map(chatbotChatRepresentation =>
        ChatbotChat.buildFrom(chatbotChatRepresentation))
    ));
  }

  public getChatListByUserID(): Observable<ChatbotChat[]> {
    return this.http.get<ChatbotChat[]>(
      API_URL + '/chats/get/user',
      {
        headers: new HttpHeaders({
          Accept: 'application/json;'
        })
      }
    ).pipe(map((chatbotChatRepresentations: ChatbotChatRepresentation[]) =>
      chatbotChatRepresentations.map(chatbotChatRepresentation =>
        ChatbotChat.buildFrom(chatbotChatRepresentation))
    ), catchError((error: HttpErrorResponse) => {
      if (error.status === 403) {
        this.router.routeToDashboard();
        return EMPTY;
      }
    }));
  }

  public deleteChatByID(id: number): Observable<number> {
    return this.http.delete<number>(
      API_URL + '/chats/delete/' + id.toString(10),
      {
        headers: new HttpHeaders({
          Accept: 'application/json;'
        })
      }
    );
  }

  public deleteAllChats(): Observable<number> {
    return this.http.delete<number>(
      API_URL + '/chats/delete_all',
      {
        headers: new HttpHeaders({
          Accept: 'application/json;'
        })
      }
    );
  }

  public generateChatTitleByID(id: number): Observable<ChatbotChat> {
    return this.http.get<ChatbotChat>(
      API_URL + '/chats/generate_title/' + id.toString(10),
      {
        headers: new HttpHeaders({
          Accept: 'application/json;'
        })
      }
    );
  }

  public updateTitleByID(chat_id: number, title: string): Observable<ChatbotChat> {
    return this.http.post<ChatbotChat>(
      API_URL + '/chats/update/title',
      {chat_id, title}
    ).pipe(map((rep: ChatbotChatRepresentation) => ChatbotChat.buildFrom(rep)));
  }

  public resetChatList(): void {
    this.chatsSubject$.next([]);
    this.paginationSubject$.next({
      loading: false,
      hasMore: true,
      currentPage: 0,
      pageSize: 12,
      nextCursor: null
    });
  }

  public loadInitialChats(): Observable<ChatbotChat[]> {
    this.isInitialLoadingSubject$.next(true);
    this.loadingSubject$.next(true);

    this.updatePaginationState({ loading: true, currentPage: 0, nextCursor: null });

    return this.getChatListPaginated().pipe(
      map(response => {
        const chats = response.items.map(rep => ChatbotChat.buildFrom(rep));
        this.chatsSubject$.next(chats);
        this.updatePaginationState({
          loading: false,
          hasMore: response.pagination.has_more,
          nextCursor: response.pagination.next_cursor,
          currentPage: 1
        });
        this.isInitialLoadingSubject$.next(false);
        this.loadingSubject$.next(false);
        return chats;
      }),
      catchError(error => {
        this.updatePaginationState({ loading: false, error });
        this.isInitialLoadingSubject$.next(false);
        this.loadingSubject$.next(false);
        throw error;
      })
    );
  }

  public loadNextPage(): Observable<ChatbotChat[]> {
    const currentPagination = this.paginationSubject$.value;

    if (!currentPagination.hasMore || currentPagination.loading) {
      return EMPTY;
    }

    this.updatePaginationState({ loading: true });

    return this.getChatListPaginated(currentPagination.nextCursor).pipe(
      map(response => {
        const currentChats = this.chatsSubject$.value;
        const newChats = response.items.map(rep => ChatbotChat.buildFrom(rep));
        const updatedChats = [...currentChats, ...newChats];

        this.chatsSubject$.next(updatedChats);
        this.updatePaginationState({
          loading: false,
          hasMore: response.pagination.has_more,
          nextCursor: response.pagination.next_cursor,
          currentPage: currentPagination.currentPage + 1
        });

        return newChats;
      }),
      catchError(error => {
        this.updatePaginationState({ loading: false, error });
        throw error;
      })
    );
  }

  public updatePaginationState(update: Partial<PaginationState>): void {
    const currentState = this.paginationSubject$.value;
    const newState = { ...currentState, ...update };
    this.paginationSubject$.next(newState);
  }

  public refreshChatList(): Observable<ChatbotChat[]> {
    this.refreshSubject$.next();
    return this.loadInitialChats();
  }

  public addSingleChat(chat: ChatbotChat): void {
    const currentChats = this.chatsSubject$.value;
    const updatedChats = [chat, ...currentChats];
    this.chatsSubject$.next(updatedChats);
  }

  private getChatListPaginated(cursor?: string | null): Observable<ChatPaginatedResponse> {
    let params = new HttpParams().set('limit', '12');

    if (cursor) {
      params = params.set('cursor', cursor);
    }

    return this.http.get<ChatPaginatedResponse>(
      API_URL + '/chats/get/user/paginated',
      {
        headers: new HttpHeaders({
          Accept: 'application/json;'
        }),
        params
      }
    ).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 403) {
          this.router.routeToDashboard();
          return EMPTY;
        }
        throw error;
      })
    );
  }

}
